import React, { useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { PropTypes } from 'prop-types';

import { useDeleteTunnelActionMutation } from 'api/main';
import { editingActionIdSet, newActionSet } from 'reducers/customTunnel';
import { customTunnelSelectors } from 'reducers';
import { useDialog } from 'utils/hooks';
import { texts } from 'utils/languages/fr';

import UpdateAction from './UpdateAction';
import { ConfirmDialog } from 'components';
import { alpha, Box, Collapse, Paper, Typography } from '@mui/material';
import * as icons from 'mdi-material-ui';

const ActionItem = ({ action, ppTunnelId, pmTunnelId }) => {
    const dispatch = useDispatch();

    const { ppId: editingActionId } = useSelector(customTunnelSelectors.getEditingActionId);
    const newAction = useSelector(customTunnelSelectors.getNewAction);

    const [deleteTunnelAction, { isLoading }] = useDeleteTunnelActionMutation();

    const { open: confirmEditOpen, onClose: handleEditClose, onOpen: handleEditOpen } = useDialog();
    const { open: deleteOpen, onClose: handleDeleteClose, onOpen: handleDeleteOpen } = useDialog();

    const handleEdit = useCallback(() => {
        dispatch(newActionSet(null));
        dispatch(editingActionIdSet({ ppId: action?.id, pmId: action?.matchingTunnelAction?.id }));
        handleEditClose();
    }, [action, dispatch, handleEditClose]);

    const handleConfirmEdit = useCallback(() => {
        if (editingActionId || newAction) {
            handleEditOpen();
        } else {
            handleEdit();
        }
    }, [editingActionId, handleEditOpen, handleEdit, newAction]);

    const handleDelete = useCallback(() => {
        deleteTunnelAction({ ppId: action?.id, pmId: action?.matchingTunnelAction?.id });
    }, [action?.id, action?.matchingTunnelAction?.id, deleteTunnelAction]);

    return (
        <Paper
            sx={theme => ({
                padding: 1,
                borderRadius: '3px',
                marginY: 1,
                border:
                    editingActionId === action?.id
                        ? `3px solid ${alpha(theme.palette.primary.light, 0.5)}`
                        : '',

                backgroundColor:
                    editingActionId !== action?.id ? theme.palette.secondary.main : 'initial',
            })}
            elevation={3}
        >
            {editingActionId !== action?.id && (
                <Box
                    sx={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                    }}
                >
                    <ConfirmDialog
                        open={confirmEditOpen}
                        handleConfirm={handleEdit}
                        handleClose={handleEditClose}
                        title={texts.CHANGES_NOT_CONFIRMED}
                        confirmText={texts.CONFIRMER}
                        cancelText={texts.CANCEL}
                    >
                        {texts.CONFIRM_DISCARD_CHANGES}
                    </ConfirmDialog>
                    <ConfirmDialog
                        open={deleteOpen}
                        handleConfirm={handleDelete}
                        handleClose={handleDeleteClose}
                        title={texts.DELETE_ACTION}
                        loading={isLoading}
                        confirmText={texts.CONFIRMER}
                        cancelText={texts.CANCEL}
                    >
                        {texts.CONFIRM_DELETE_ACTION}
                    </ConfirmDialog>
                    <Typography title={action.name} noWrap sx={{ maxWidth: '50%' }}>
                        {action.name}
                    </Typography>
                    <Box display="flex" alignItems="center">
                        <icons.Pencil
                            sx={{ cursor: 'pointer', '&:hover': { color: 'primary.main' } }}
                            onClick={handleConfirmEdit}
                        />
                        <icons.Delete
                            sx={{ cursor: 'pointer', ':hover': { color: 'error.main' } }}
                            onClick={handleDeleteOpen}
                        />
                    </Box>
                </Box>
            )}
            <Collapse in={editingActionId === action?.id}>
                {editingActionId === action?.id && (
                    <UpdateAction action={action} ppTunnelId={ppTunnelId} pmTunnelId={pmTunnelId} />
                )}
            </Collapse>
            {action?.title}
        </Paper>
    );
};

ActionItem.propTypes = {
    action: PropTypes.object,
    ppTunnelId: PropTypes.number,
    pmTunnelId: PropTypes.number,
};

export default ActionItem;
